export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: 'follower' | 'notification' | 'task' | 'general';
  icon: string;
  steps: Array<{
    type: 'trigger' | 'action';
    triggerType?: string;
    actionType?: string;
    config: any;
    position: number;
  }>;
}

export const followerAutomationTemplates: AutomationTemplate[] = [
  {
    id: 'sales-team-auto-follow',
    name: 'Sales Team Auto-Follow',
    description: 'Automatically add all sales team members as followers when a project is created',
    category: 'follower',
    icon: 'Users',
    steps: [
      {
        type: 'trigger',
        triggerType: 'project_created',
        config: {
          trigger_type: 'project_created',
          filters: {
            pipeline_ids: [],
            stage_ids: [],
            team_ids: [],
            from_stage_ids: [],
            to_stage_ids: []
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'project_owner_team',
          user_ids: [],
          team_ids: [],
          role_ids: [],
          notify_on_add: true,
          include_project_owner_team: true
        },
        position: 1
      }
    ]
  },
  {
    id: 'manager-oversight',
    name: 'Manager Oversight',
    description: 'Add sales managers as followers to all new projects for oversight',
    category: 'follower',
    icon: 'Shield',
    steps: [
      {
        type: 'trigger',
        triggerType: 'project_created',
        config: {
          trigger_type: 'project_created',
          filters: {
            pipeline_ids: [],
            stage_ids: [],
            team_ids: [],
            from_stage_ids: [],
            to_stage_ids: []
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'role_based',
          user_ids: [],
          team_ids: [],
          role_ids: ['sales_manager', 'admin'],
          notify_on_add: true,
          include_project_owner_team: false
        },
        position: 1
      }
    ]
  },
  {
    id: 'stage-escalation',
    name: 'Stage Escalation',
    description: 'Add senior team members when projects reach critical stages',
    category: 'follower',
    icon: 'TrendingUp',
    steps: [
      {
        type: 'trigger',
        triggerType: 'stage_changed',
        config: {
          trigger_type: 'stage_changed',
          filters: {
            pipeline_ids: [],
            stage_ids: [],
            team_ids: [],
            from_stage_ids: [],
            to_stage_ids: [] // User should configure this to select critical stages
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'role_based',
          user_ids: [],
          team_ids: [],
          role_ids: ['senior_sales', 'sales_manager'],
          notify_on_add: true,
          include_project_owner_team: false
        },
        position: 1
      }
    ]
  },
  {
    id: 'team-assignment-follow',
    name: 'Team Assignment Follow',
    description: 'Automatically add team members as followers when a project is assigned to their team',
    category: 'follower',
    icon: 'UserPlus',
    steps: [
      {
        type: 'trigger',
        triggerType: 'project_assigned',
        config: {
          trigger_type: 'project_assigned',
          filters: {
            pipeline_ids: [],
            stage_ids: [],
            team_ids: [], // Can be configured to specific teams
            from_stage_ids: [],
            to_stage_ids: []
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'team_members',
          user_ids: [],
          team_ids: [], // Will be populated dynamically based on assignment
          role_ids: [],
          notify_on_add: true,
          include_project_owner_team: false
        },
        position: 1
      }
    ]
  },
  {
    id: 'pipeline-specific-follow',
    name: 'Pipeline-Specific Follow',
    description: 'Add specific users as followers based on which pipeline a project enters',
    category: 'follower',
    icon: 'GitBranch',
    steps: [
      {
        type: 'trigger',
        triggerType: 'pipeline_entered',
        config: {
          trigger_type: 'pipeline_entered',
          filters: {
            pipeline_ids: [], // User should configure specific pipelines
            stage_ids: [],
            team_ids: [],
            from_stage_ids: [],
            to_stage_ids: []
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'specific_users',
          user_ids: [], // User should configure specific users
          team_ids: [],
          role_ids: [],
          notify_on_add: true,
          include_project_owner_team: false
        },
        position: 1
      }
    ]
  },
  {
    id: 'cross-team-collaboration',
    name: 'Cross-Team Collaboration',
    description: 'Add members from multiple teams to foster collaboration',
    category: 'follower',
    icon: 'Users',
    steps: [
      {
        type: 'trigger',
        triggerType: 'project_created',
        config: {
          trigger_type: 'project_created',
          filters: {
            pipeline_ids: [],
            stage_ids: [],
            team_ids: [],
            from_stage_ids: [],
            to_stage_ids: []
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          follower_source: 'team_members',
          user_ids: [],
          team_ids: [], // User should configure multiple teams
          role_ids: [],
          notify_on_add: true,
          include_project_owner_team: true
        },
        position: 1
      }
    ]
  }
];

export const contractAutomationTemplates: AutomationTemplate[] = [
  {
    id: 'contract-sent-notification',
    name: 'Contract Sent Notification',
    description: 'Notify stakeholders when contract is sent to customer',
    category: 'notification',
    icon: 'Mail',
    steps: [
      {
        type: 'trigger',
        triggerType: 'contract_sent',
        config: {
          trigger_type: 'contract_sent',
          filters: {
            contract_types: [],
            project_ids: [],
            pipeline_ids: [],
            team_ids: [],
            sent_statuses: ['Sent to Customer', 'Pending Signature 1']
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'notification',
        config: {
          title: 'Contract Sent',
          message: 'Contract has been sent to customer for signing',
          priority: 'normal',
          send_to_followers: true,
          send_to_assignee: true,
          send_to_owner: false
        },
        position: 1
      }
    ]
  },
  {
    id: 'contract-signed-progression',
    name: 'Auto-Advance on Contract Signing',
    description: 'Automatically advance project to next stage when contract is signed',
    category: 'general',
    icon: 'CheckCircle',
    steps: [
      {
        type: 'trigger',
        triggerType: 'contract_signed',
        config: {
          trigger_type: 'contract_signed',
          filters: {
            contract_types: [],
            project_ids: [],
            pipeline_ids: [],
            team_ids: [],
            completion_level: 'complete_only'
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'update_stage',
        config: {
          use_stage_name: true,
          stage_name: 'Contract Signed'
        },
        position: 1
      }
    ]
  },
  {
    id: 'contract-oversight',
    name: 'Contract Oversight',
    description: 'Add managers as followers when high-value contracts are sent',
    category: 'follower',
    icon: 'Users',
    steps: [
      {
        type: 'trigger',
        triggerType: 'contract_sent',
        config: {
          trigger_type: 'contract_sent',
          filters: {
            contract_types: ['TurnKey'], // High-value contracts
            project_ids: [],
            pipeline_ids: [],
            team_ids: [],
            sent_statuses: ['Sent to Customer', 'Pending Signature 1']
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          selected_roles: ['Internal PM Manager'],
          user_ids: [],
          notify_on_add: true
        },
        position: 1
      }
    ]
  },
  {
    id: 'contract-completion-handoff',
    name: 'Contract Completion Handoff',
    description: 'Add fulfillment team when contract is fully signed',
    category: 'follower',
    icon: 'UserPlus',
    steps: [
      {
        type: 'trigger',
        triggerType: 'contract_signed',
        config: {
          trigger_type: 'contract_signed',
          filters: {
            contract_types: [],
            project_ids: [],
            pipeline_ids: [],
            team_ids: [],
            completion_level: 'complete_only'
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'add_followers',
        config: {
          selected_roles: ['Internal PM', 'Fulfillment Team'],
          user_ids: [],
          notify_on_add: true
        },
        position: 1
      }
    ]
  },
  {
    id: 'urgent-contract-notification',
    name: 'Urgent Contract Notification',
    description: 'Send urgent notifications for high-priority contract events',
    category: 'notification',
    icon: 'AlertTriangle',
    steps: [
      {
        type: 'trigger',
        triggerType: 'contract_signed',
        config: {
          trigger_type: 'contract_signed',
          filters: {
            contract_types: ['TurnKey'],
            project_ids: [],
            pipeline_ids: [],
            team_ids: [],
            completion_level: 'complete_only'
          }
        },
        position: 0
      },
      {
        type: 'action',
        actionType: 'notification',
        config: {
          title: 'High-Value Contract Signed',
          message: 'Important: TurnKey contract has been fully executed and requires immediate attention',
          priority: 'urgent',
          send_to_followers: true,
          send_to_assignee: true,
          send_to_owner: true
        },
        position: 1
      }
    ]
  }
];

export const allAutomationTemplates: AutomationTemplate[] = [
  ...followerAutomationTemplates,
  ...contractAutomationTemplates,
  // Other template categories can be added here in the future
];